import dayjs from 'dayjs'
import { DEFAULT_LOGISTICS_SERVICE_TYPE, DEFAULT_PAGE_SIZE, FIELD_NAMES, ReturnManagementFormValues } from './constants'
import { toSingleValue } from './filterToRequest'

const DATE_FORMAT = 'YYYYMMDD'

/**
 * 메뉴 세션·URL 쿼리에 싣는 조회 조건 모양(D16 — `@/hooks/useSessionPersistedState` 폐기, 기존
 * `searchQuery.ts` 패턴을 그대로 쓴다). 선례: `erpInbound/searchQuery.ts`.
 *
 * 조회 조건뿐 아니라 페이지 번호와 목록 검색어까지 싣는다. 조건만 남고 보던 페이지가 1로 돌아가면
 * 사용자에게는 여전히 새로고침으로 보인다.
 *
 * D2 로 `FilterArea` + 외부 antd Form 을 써서 `onFormReady(form)` 로 받은 form 인스턴스에
 * `setFieldsValue` 로 값을 심을 수 있다 — erpInbound 가 겪은 "통로 없는 필드" 문제가 없어
 * 필터 필드 4개를 전부 저장·복원한다.
 */
export interface ReturnManagementSearchQuery {
  logisticsServiceTypes?: string[]
  shipperCodes?: string
  plnDateFrom?: string
  plnDateTo?: string
  insBy?: string
  rlChannel?: string
  /** 1-based 페이저 표기 그대로 싣는다 — URL 만 봐도 몇 페이지인지 읽힌다. */
  page?: string
  size?: string
  /** 표 위 목록 검색어. 현재 페이지 행만 거르는 클라이언트 필터다. */
  keyword?: string
}

/** 화면 상태 → 저장 형태. 빈 값은 키 자체를 빼 쿼리스트링을 짧게 유지한다. */
export const toSearchQuery = (state: {
  values: ReturnManagementFormValues
  page: number
  pageSize: number
  keyword: string
}): ReturnManagementSearchQuery => {
  const [plnFrom, plnTo] = state.values[FIELD_NAMES.plnDateRange] ?? [null, null]
  const types = state.values[FIELD_NAMES.logisticsServiceTypes] ?? []
  const shipperCodes = toSingleValue(state.values[FIELD_NAMES.shipperCodes])
  const insBy = toSingleValue(state.values[FIELD_NAMES.insBy])
  const rlChannel = toSingleValue(state.values[FIELD_NAMES.rlChannel])

  return {
    ...(types.length ? { logisticsServiceTypes: types } : {}),
    ...(shipperCodes ? { shipperCodes } : {}),
    ...(plnFrom ? { plnDateFrom: plnFrom.format(DATE_FORMAT) } : {}),
    ...(plnTo ? { plnDateTo: plnTo.format(DATE_FORMAT) } : {}),
    ...(insBy ? { insBy } : {}),
    ...(rlChannel ? { rlChannel } : {}),
    ...(state.page > 1 ? { page: String(state.page) } : {}),
    ...(state.pageSize === DEFAULT_PAGE_SIZE ? {} : { size: String(state.pageSize) }),
    ...(state.keyword ? { keyword: state.keyword } : {}),
  }
}

/** 문자열이 아닌 값(배열 등 조작된 URL)은 무시한다 — 화면이 깨지지 않게 한다. */
const text = (value: unknown) => (typeof value === 'string' && value ? value : undefined)

/** `qs` 는 `comma:true` 라도 단일 값이면 배열이 아니라 문자열로 돌려줄 수 있다 — 양쪽 다 받는다. */
const stringArray = (value: unknown): string[] => {
  if (Array.isArray(value)) return value.filter((v): v is string => typeof v === 'string' && v !== '')
  if (typeof value === 'string' && value) return value.split(',').filter(Boolean)
  return []
}

/** 1 이상 정수만 받는다. 0·음수·소수·문자는 기본값으로 되돌린다. */
const positiveInt = (value: unknown, fallback: number) => {
  const raw = text(value)
  if (!raw || !/^\d+$/.test(raw)) return fallback
  const parsed = Number(raw)
  return parsed >= 1 ? parsed : fallback
}

export interface RestoredReturnManagementState {
  values: ReturnManagementFormValues
  page: number
  pageSize: number
  keyword: string
  /** 복원할 조회 조건이 실제로 있었는지. 있을 때만 마운트 시점에 조회를 돌린다. */
  hasQuery: boolean
}

/**
 * 저장된 쿼리를 화면 상태로 되돌린다.
 *
 * 화주유형이 비면 기본값(`DEFAULT_LOGISTICS_SERVICE_TYPE` = M1P)으로 채운다. 화주코드가 비면 세션
 * 화주(`defaultShipperCode`)로 채운다. 반품일자가 없으면 오늘~오늘(화면 기본값).
 * `hasQuery` 는 URL 에 화주코드가 있었는지로 판정한다 — 최초 진입(빈 URL)에서는 false 라 지금처럼
 * 빈 표로 열리고, 탭을 다녀온 경우에만 조회가 자동으로 돈다.
 */
export const fromSearchQuery = (query: Partial<ReturnManagementSearchQuery>, defaultShipperCode: string): RestoredReturnManagementState => {
  const shipperCodes = text(query.shipperCodes)
  const plnDateFrom = text(query.plnDateFrom)
  const plnDateTo = text(query.plnDateTo)
  const plnDateRange: [dayjs.Dayjs, dayjs.Dayjs] =
    plnDateFrom && plnDateTo ? [dayjs(plnDateFrom, DATE_FORMAT), dayjs(plnDateTo, DATE_FORMAT)] : [dayjs(), dayjs()]

  const logisticsServiceTypes = stringArray(query.logisticsServiceTypes)

  return {
    values: {
      logisticsServiceTypes: logisticsServiceTypes.length ? logisticsServiceTypes : [DEFAULT_LOGISTICS_SERVICE_TYPE],
      shipperCodes: shipperCodes ?? defaultShipperCode,
      plnDateRange,
      insBy: text(query.insBy) ?? '',
      rlChannel: text(query.rlChannel) ?? '',
    },
    page: positiveInt(query.page, 1),
    pageSize: positiveInt(query.size, DEFAULT_PAGE_SIZE),
    keyword: text(query.keyword) ?? '',
    hasQuery: !!shipperCodes,
  }
}
